import { useEffect, useRef, useState } from 'react'
import { DayPicker, DayButton, getDefaultClassNames, ClassNames, UI, CustomComponents } from 'react-day-picker'
import { Button, IconButton, Input } from '@shopflo/ui'
import type { HTMLAttributes, SelectHTMLAttributes } from 'react'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectScrollUpButton,
  SelectScrollDownButton
} from '@shopflo/ui'
import { classNames as cn } from 'src/lib/utils/helpers'
import { CaretLeft, CaretRight, CaretUpDown } from '@phosphor-icons/react'

function CalendarDayButton({ className, day, modifiers, ...props }: React.ComponentProps<typeof DayButton>) {
  const ref = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  const isSelected = modifiers.selected
  const isRangeStart = modifiers.range_start
  const isRangeEnd = modifiers.range_end
  const isRangeMiddle = modifiers.range_middle
  const isOutside = modifiers.outside
  const isDisabled = modifiers.disabled

  const isInRange = isSelected || isRangeStart || isRangeEnd || isRangeMiddle

  return (
    <button
      ref={ref}
      data-day={day.date.toLocaleDateString()}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-full transition-colors bg-white cursor-pointer',
        isOutside ? 'text-zinc-400 ' : ' ',
        isInRange && !isOutside ? '!bg-indigo-500 hover:bg-indigo-500' : '',
        isDisabled ? 'opacity-50 !cursor-not-allowed ' : 'hover:bg-zinc-950/5',
        className ?? ''
      )}
      {...props}
    >
      <span
        className={cn(
          'text-text-sm-regular',
          isOutside ? 'text-zinc-400' : 'text-zinc-600',
          isInRange && !isOutside ? '!text-white' : ''
        )}
      >
        {day.date.getDate()}
      </span>
    </button>
  )
}

function NextMonthButton(props: React.ComponentProps<'button'>) {
  return (
    <IconButton
      {...props}
      IconComponent={CaretRight}
      size='sm'
      variant='ghost'
      iconProps={{ weight: 'bold' }}
      className='text-zinc-400 mt-0.5 pointer-events-auto'
    />
  )
}

function PreviousMonthButton(props: React.ComponentProps<'button'>) {
  return (
    <IconButton
      {...props}
      IconComponent={CaretLeft}
      size='sm'
      variant='ghost'
      iconProps={{ weight: 'bold' }}
      className='text-zinc-400 mt-0.5 pointer-events-auto'
    />
  )
}

function CustomDropDown(
  props: {
    options?: { label: string; value: string | number; disabled?: boolean }[]
    className?: string
    classNames?: ClassNames
    components?: CustomComponents
  } & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'>
) {
  const options = props.options || []
  const className = props.className
  const classNames = props.classNames || {}
  const value = props.value
  const onChange = props.onChange
  const disabled = props.disabled

  function handleChange(val: string) {
    const numericValue =
      options.find(function (o) {
        return o.value.toString() === val
      })?.value ?? Number(val)
    if (onChange) {
      const event = {
        target: { value: numericValue.toString() }
      } as React.ChangeEvent<HTMLSelectElement>
      onChange(event)
    }
  }

  const dropdownClassName = (classNames as Record<string, string | undefined>)?.[UI.DropdownRoot] ?? ''

  return (
    <span data-disabled={disabled ? 'true' : undefined} className={cn(dropdownClassName, className ?? '')}>
      <Select value={value?.toString() ?? ''} onValueChange={handleChange} disabled={disabled}>
        <SelectTrigger className='w-full h-8 gap-1.5 text-text-sm-medium text-zinc-600 rounded-lg border-zinc-200 transition-colors hover:bg-zinc-50'>
          <SelectValue placeholder='Select...' />
        </SelectTrigger>
        <SelectContent className='max-h-60 rounded-xl border-none shadow-xl'>
          <SelectScrollUpButton />
          {options.map(function (opt) {
            return (
              <SelectItem key={opt.value} value={opt.value.toString()} disabled={opt.disabled} className='rounded-lg'>
                {opt.label}
              </SelectItem>
            )
          })}
          <SelectScrollDownButton />
        </SelectContent>
      </Select>
    </span>
  )
}

export function Weekdays(props: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <thead aria-hidden className='flex justify-between w-full'>
      <tr {...props} className='flex flex-row w-full justify-start gap-0.5' />
    </thead>
  )
}

export function Weekday(props: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return <th {...props} className='w-8 mb-1 mt-2 item-center text-text-xs-medium text-zinc-500' />
}

export function Week(props: React.HTMLAttributes<HTMLTableRowElement>) {
  return <tr {...props} className='mt-1 flex justify-start' />
}

function TimePicker({ date, onChange }: { date: Date; onChange: (date: Date) => void }) {
  const currentHours = date.getHours()
  const displayHours = ((currentHours + 11) % 12) + 1
  const displayMinutes = date.getMinutes()
  const displayAMPM = currentHours >= 12 ? 'PM' : 'AM'

  const [hourInput, setHourInput] = useState(displayHours.toString())
  const [minuteInput, setMinuteInput] = useState(displayMinutes.toString())

  useEffect(() => {
    setHourInput(displayHours.toString())
  }, [displayHours])

  useEffect(() => {
    setMinuteInput(displayMinutes.toString())
  }, [displayMinutes])

  const updateDate = (newHour: number, newMinute: number, ampm: string) => {
    const newDate = new Date(date)
    let finalHours = newHour
    if (ampm === 'PM' && newHour < 12) finalHours += 12
    if (ampm === 'AM' && newHour === 12) finalHours = 0
    newDate.setHours(finalHours)
    newDate.setMinutes(newMinute)
    onChange(newDate)
  }

  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    if (inputValue === '') {
      setHourInput('')
      return
    }
    const parsedHour = parseInt(inputValue)
    if (isNaN(parsedHour) || parsedHour < 1) return

    const finalHour = Math.min(parsedHour, 12)
    setHourInput(finalHour.toString())
    updateDate(finalHour, displayMinutes, displayAMPM)
  }

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    if (inputValue === '') {
      setMinuteInput('')
      return
    }
    const parsedMinute = parseInt(inputValue)
    if (isNaN(parsedMinute) || parsedMinute < 0) return

    const finalMinute = Math.min(parsedMinute, 59)
    setMinuteInput(finalMinute.toString())
    updateDate(displayHours, finalMinute, displayAMPM)
  }

  const toggleAMPM = () => {
    updateDate(displayHours, displayMinutes, displayAMPM === 'AM' ? 'PM' : 'AM')
  }

  return (
    <div className='flex items-center gap-1.5 p-3 bg-zinc-50/50 border-t-1 border-zinc-500/10 w-full'>
      <div className='flex items-center justify-center gap-2 w-full'>
        <div>
          <Input
            value={hourInput}
            onChange={handleHourChange}
            className='text-center h-8 w-20'
            maxLength={2}
            dimension='sm'
          />
        </div>
        <div>
          <Input
            value={minuteInput}
            onChange={handleMinuteChange}
            className='text-center h-8 w-20'
            maxLength={2}
            dimension='sm'
          />
        </div>
        <div className='flex-1'>
          <Button variant='outline' size='sm' onClick={toggleAMPM} className='h-8 px-0 w-full' rightIcon={CaretUpDown}>
            {displayAMPM}
          </Button>
        </div>
      </div>
    </div>
  )
}

function isSingleMode(props: React.ComponentProps<typeof DayPicker>) {
  return props.mode === 'single'
}

export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'dropdown',
  buttonVariant = 'ghost',
  formatters,
  components,
  showTime = false,
  clearOptions,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant']
  showTime?: boolean
  clearOptions?: {
    shouldShowClearButton?: boolean
    handleClear?: () => void
  }
}) {
  const defaultClassNames = getDefaultClassNames()

  const singleMode = isSingleMode(props)
  const selectedDate = singleMode ? props.selected : undefined
  const onSelectDate = singleMode ? props.onSelect : undefined

  return (
    <div className='flex flex-col w-fit'>
      <DayPicker
        showOutsideDays={showOutsideDays}
        className={cn('bg-background group/calendar p-3 [--cell-size:2rem]', className ?? '')}
        captionLayout={captionLayout}
        startMonth={new Date(new Date().getFullYear() - 1, 0)}
        endMonth={new Date(new Date().getFullYear() + 10, 11)}
        formatters={{
          formatMonthDropdown: date => date.toLocaleString('default', { month: 'short' }),
          formatYearDropdown: date => date.getFullYear().toString(),
          ...formatters
        }}
        classNames={{
          root: cn('w-fit ', defaultClassNames.root),
          months: cn('relative flex flex-col gap-2 md:flex-row', defaultClassNames.months),
          month: cn('flex w-full flex-col gap-2', defaultClassNames.month),
          nav: cn(
            'pointer-events-none absolute inset-x-0 top-0 flex w-full items-center justify-between gap-3',
            defaultClassNames.nav
          ),

          month_caption: cn(
            'flex h-[--cell-size] w-full items-center justify-center px-[--cell-size]',
            defaultClassNames.month_caption
          ),
          dropdowns: cn(
            'flex h-[--cell-size] w-full items-center justify-center gap-2 text-sm font-medium',
            defaultClassNames.dropdowns
          ),
          ...classNames
        }}
        components={{
          NextMonthButton: NextMonthButton,
          PreviousMonthButton: PreviousMonthButton,
          DayButton: CalendarDayButton,
          Dropdown: CustomDropDown,
          Weekday: Weekday,
          Weekdays: Weekdays,
          Week: Week,
          ...components
        }}
        {...props}
      />
      {showTime && selectedDate && onSelectDate && (
        <TimePicker
          date={selectedDate}
          onChange={newDate => onSelectDate(newDate, newDate, {}, {} as React.MouseEvent)}
        />
      )}

      {clearOptions?.shouldShowClearButton ? (
        <div className='flex justify-end px-2 pb-2'>
          <Button variant='outline' size='sm' className='w-full ' onClick={clearOptions.handleClear}>
            Clear
          </Button>
        </div>
      ) : null}
    </div>
  )
}
